import { QueryKey, useQuery, UseQueryOptions } from 'react-query';
import { Title } from '../../titles/types/title';
import { useSubscribedProducts } from '../products/hooks';
import type { Provider } from './types';
import { providersQuery } from './queries';
import { selectProvider } from './utils';
import { useMostRecentBookmark } from '../bookmarks/hooks';
import { useSectionContext } from '../../../../views/ats-pages/section/section-context';

export function useProviders<T = Provider[]>(options?: UseQueryOptions<Provider[], unknown, T, QueryKey>) {
  return useQuery(providersQuery(options));
}

interface BaseProviderResult {
  provider: Provider | undefined;
  error: Error | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

interface TitleProviderLoadingResult extends BaseProviderResult {
  provider: undefined;
  error: undefined;
  isLoading: true;
  isSuccess: false;
  isError: false;
}

interface TitleProviderErrorResult extends BaseProviderResult {
  provider: undefined;
  error: Error;
  isLoading: false;
  isSuccess: false;
  isError: true;
}

interface TitleProviderSuccessResult extends BaseProviderResult {
  provider: Provider | undefined;
  error: undefined;
  isLoading: false;
  isSuccess: true;
  isError: false;
}

type UseTitleProviderResult = TitleProviderLoadingResult | TitleProviderErrorResult | TitleProviderSuccessResult;

export function useTitleProvider(title: Title): UseTitleProviderResult {
  const { data: providers, error: providersError, isLoading: isProvidersLoading } = useProviders();
  const { data: products = [], error: prodcutsError, isLoading: isProductsLoading } = useSubscribedProducts();
  const { data: bookmark, error: bookmarkError, isLoading: isBookmarkLoading } = useMostRecentBookmark(title.masterId);
  const section = useSectionContext('useTitleProvider');

  const isLoading = isProvidersLoading || isProductsLoading || isBookmarkLoading;
  const errors = [providersError, prodcutsError, bookmarkError] as const;

  if (errors.some((e) => e !== null)) {
    throw new AggregateError(errors.filter((e) => e !== null));
  }

  if (isLoading) {
    return { provider: undefined, error: undefined, isLoading: true, isSuccess: false, isError: false };
  }

  if (providers === undefined) {
    throw new Error('Expected providers to be defined');
  }

  const selectedProvider =
    section.style.type === 'continue-watching' && bookmark !== undefined
      ? providers.find((p) => p.id === bookmark.providerIdentifier)
      : selectProvider({ title, providers, products, bookmark });

  return { provider: selectedProvider, error: undefined, isLoading: false, isSuccess: true, isError: false };
}
